
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { PlusCircle, Trash2, Pencil } from "lucide-react";
import { AddCommentModal } from "@/components/candidates/AddCommentModal";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";
import { CommentContent } from "@/components/comments/CommentContent";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface MatchCommentsProps {
	matchId: string | undefined;
}

interface MatchComment {
	id: string;
	content: string;
	created_by: string;
	created_by_uuid: string | null;
	created_at: string;
	updated_at: string;
	migration?: boolean;
	profiles?: {
		first_name: string | null;
		last_name: string | null;
	};
}

export const MatchComments = ({ matchId }: MatchCommentsProps) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const [comments, setComments] = useState<MatchComment[]>([]);
	const [loading, setLoading] = useState(true);
	const [isAddCommentOpen, setIsAddCommentOpen] = useState(false);
	const [commentToDelete, setCommentToDelete] = useState<MatchComment | null>(null);
	const [commentToEdit, setCommentToEdit] = useState<MatchComment | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const fetchComments = async () => {
		if (!matchId) return;

		try {
			setLoading(true);
			const { data, error } = await supabase
				.from('match_comments')
				.select(`
					*,
					profiles:created_by_uuid (
						first_name,
						last_name
					)
				`)
				.eq('match_id', matchId)
				.order('created_at', { ascending: false });

			if (error) throw error;

			setComments(data || []);
		} catch (error) {
			console.error('Error fetching match comments:', error);
			toast({
				title: "Error",
				description: "Could not load comments",
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchComments();
	}, [matchId]);

	const handleAddComment = async (content: string) => {
		if (!matchId || !content || !user?.id) return;

		try {
			const { data, error } = await supabase
				.from('match_comments')
				.insert({
					match_id: matchId,
					content,
					created_by: "Current User", // Keep for backward compatibility
					created_by_uuid: user.id, // New UUID field
				})
				.select();

			if (error) throw error;

			toast({
				title: "Success",
				description: "Comment added successfully",
			});

			// Refresh comments
			fetchComments();
		} catch (error) {
			console.error('Error adding comment:', error);
			toast({
				title: "Error",
				description: "Could not add comment",
				variant: "destructive",
			});
		} finally {
			setIsAddCommentOpen(false);
		}
	};

	const handleEditComment = async (content: string) => {
		if (!commentToEdit || !matchId || !content) return;

		try {
			const { error } = await supabase
				.from('match_comments')
				.update({ 
					content,
					updated_at: new Date().toISOString()
				})
				.eq('id', commentToEdit.id);

			if (error) throw error;

			toast({
				title: "Success",
				description: "Comment updated successfully",
			});

			// Refresh comments
			fetchComments();
		} catch (error) {
			console.error('Error updating comment:', error);
			toast({
				title: "Error",
				description: "Could not update comment",
				variant: "destructive",
			});
		} finally {
			setCommentToEdit(null);
		}
	};

	const handleDeleteComment = async () => {
		if (!commentToDelete || !matchId) return;

		try {
			setIsDeleting(true);
			
			const { error } = await supabase
				.from('match_comments')
				.delete()
				.eq('id', commentToDelete.id);

			if (error) throw error;

			toast({
				title: "Success",
				description: "Comment deleted successfully",
			});

			// Remove comment from local state
			setComments(prev => prev.filter(comment => comment.id !== commentToDelete.id));
			setCommentToDelete(null);
		} catch (error) {
			console.error('Error deleting comment:', error);
			toast({
				title: "Error",
				description: "Could not delete comment",
				variant: "destructive",
			});
		} finally {
			setIsDeleting(false);
		}
	};

	const getUserDisplayName = (comment: MatchComment) => {
		// Check if this is a migrated comment
		if (comment.migration === true) {
			return {
				name: "Import",
				isItalic: true
			};
		}
		
		let displayName = "Unknown User";
		
		// First try to get name from joined profile data
		if (comment.profiles) {
			const fullName = `${comment.profiles.first_name || ''} ${comment.profiles.last_name || ''}`.trim();
			if (fullName) displayName = fullName;
		}
		
		// Fall back to the old created_by field for backward compatibility
		if (displayName === "Unknown User" && comment.created_by && comment.created_by !== "Current User") {
			displayName = comment.created_by;
		}
		
		return {
			name: displayName,
			isItalic: false
		};
	};

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<h3 className="text-lg font-semibold">{t("matches.comments.title", "Comments")}</h3>
				<Button 
					onClick={() => setIsAddCommentOpen(true)}
					className="flex items-center gap-1"
				>
					<PlusCircle className="h-4 w-4" />
					<span>{t("matches.comments.addComment", "Add Comment")}</span>
				</Button>
			</div>

			{loading ? (
				<div className="flex justify-center p-8">
					<div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
				</div>
			) : (
				<>
					{comments.length === 0 ? (
						<p className="text-gray-500 dark:text-gray-400 italic text-center py-8">{t("matches.comments.noComments", "No comments yet")}</p>
					) : (
						<div className="space-y-4">
							{comments.map((comment) => (
								<div key={comment.id} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-sm">
									<div className="flex justify-between mb-2">
										<span className={`font-medium ${getUserDisplayName(comment).isItalic ? 'italic' : ''}`}>
											{getUserDisplayName(comment).name}
										</span>
										<div className="flex items-center gap-2">
											<span className="text-sm text-gray-500 dark:text-gray-400">
												{new Date(comment.created_at).toLocaleString()}
											</span>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => setCommentToEdit(comment)}
												className="h-6 w-6 p-0 text-gray-400 hover:text-blue-500"
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => setCommentToDelete(comment)}
												className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									</div>
									<CommentContent content={comment.content} />
								</div>
							))}
						</div>
					)}
				</>
			)}

			<AddCommentModal
				isOpen={isAddCommentOpen}
				onClose={() => setIsAddCommentOpen(false)}
				onSave={handleAddComment}
			/>

			<AddCommentModal
				isOpen={!!commentToEdit}
				onClose={() => setCommentToEdit(null)}
				onSave={handleEditComment}
				initialContent={commentToEdit?.content || ""}
				isEditing={true}
			/>

			<AlertDialog open={!!commentToDelete} onOpenChange={(open) => !open && setCommentToDelete(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete Comment</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete this comment? This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction 
							onClick={handleDeleteComment}
							disabled={isDeleting}
							className="bg-red-600 hover:bg-red-700"
						>
							{isDeleting ? "Deleting..." : "Delete"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};
